<script setup lang="ts">
import type { Props } from '@/shared/ui/AppButton/model/types/appButton';

const props: Props = withDefaults(defineProps<Props>(), {
  to: '',
  isBig: false,
  isSmall: false,
  isBordered: false,
  isWhite: false,
  type: 'button',
  buttonText: '',
  isNeedAuth: false,
  tag: 'button',
  isFullWidth: false,
  disabled: false,
  leftIconName: '',
  leftIconWidth: 'auto',
  leftIconHeight: 'auto',
  rightIconName: '',
  rightIconWidth: 'auto',
  rightIconHeight: 'auto',
  supText: '',
  isIconStroke: false,
  isHoverPulseEffect: false,
});

const emit: (e: 'click') => void = defineEmits(['click']);

const isMobile: boolean = useIsMobile();
const isTouched: Ref<boolean> = ref(false);
const isHoverPulse: Ref<boolean> = ref(false);

const nuxtLink = resolveComponent('NuxtLink');

const component: ComputedRef<Component | string> = computed(() => {
  if (props.to || props.tag === 'NuxtLink') {
    return nuxtLink;
  }
  return props.tag || 'button';
});

const clickHandler: (e: Event) => void = (e: Event) => {
  if (props.disabled) {
    e.preventDefault();
    return;
  }

  if (props.isNeedAuth) {
    e.preventDefault();
    return false;
  }

  emit('click');
};

const isNativeButton: ComputedRef<boolean> = computed(
  () => !props.to && props.tag !== 'NuxtLink',
);

const supPlainText: ComputedRef<string> = computed(() =>
  (props.supText ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .trim(),
);

const buttonAriaLabel: ComputedRef<string | undefined> = computed(
  () => [props.buttonText, supPlainText.value].filter(Boolean).join(' ') || undefined,
);

const touchStartHandler: () => void = () => {
  isTouched.value = true;

  if (props.isHoverPulseEffect) {
    isHoverPulse.value = true;
  }
};

const hoverStartHandler: () => void = () => {
  if (props.isHoverPulseEffect) {
    isHoverPulse.value = true;
  }
};

const touchEndHandler: () => void = () => {
  isTouched.value = false;

  if (props.isHoverPulseEffect) {
    isHoverPulse.value = false;
  }
};

const hoverEndHandler: () => void = () => {
  if (props.isHoverPulseEffect) {
    isHoverPulse.value = false;
  }
};
</script>

<template>
  <component
    :is="component"
    ref="button"
    :class="[
      'app-button',
      { _mobile: isMobile },
      { '_white-bg': isWhite },
      { _big: isBig },
      { _small: isSmall },
      { _bordered: isBordered },
      { _link: !!to },
      { _disabled: disabled },
      { _full: isFullWidth },
      { _touched: isTouched },
      { _stroke: isIconStroke },
      { _pulse: isHoverPulse },
    ]"
    :to="to || false"
    :type="type"
    :disabled="isNativeButton && disabled ? true : undefined"
    :aria-label="buttonAriaLabel"
    @click="clickHandler"
    @mouseover="hoverStartHandler"
    @mouseleave="hoverEndHandler"
  >
    <span
      class="app-button__touch"
      data-testid="button-touch"
      @touchstart="touchStartHandler"
      @touchend="touchEndHandler"
    >
    </span>
    <AppIcon
      v-if="leftIconName"
      :name="leftIconName"
      class="app-button__icon"
      :style="{ width: `${leftIconWidth}px`, height: `${leftIconHeight}px` }"
    />
    <span v-if="buttonText" class="app-button__text" data-testid="button-text">
      {{ buttonText }}
    </span>
    <sup
      v-if="supText"
      class="app-button__sup"
      data-testid="button-sup"
      v-html="supText"
    ></sup>
    <AppIcon
      v-if="rightIconName"
      :name="rightIconName"
      class="app-button__icon"
      :style="{ width: `${rightIconWidth}px`, height: `${rightIconHeight}px` }"
    />
  </component>
</template>

<style lang="less">
.app-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  vertical-align: top;
  gap: 5px;
  background-color: @color-persimmon;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  width: 100%;
  border-radius: @main-size;
  padding: @size-x3 @size-x4;
  transition: all @A linear;
  min-height: 44px;
  position: relative;

  &._link {
    .a-reset();
  }

  &:not(._mobile):hover {
    background-color: @color-flame-pea;
    -webkit-background-color: @color-flame-pea;
  }

  &._big {
    width: 236px;
  }

  &._small {
    width: 150px;
  }

  &._bordered {
    border: 1px solid @color-persimmon;

    &:not(._mobile):hover {
      color: #fff;
      border-color: @color-flame-pea;
      background-color: @color-flame-pea;
      -webkit-color: #fff;
      -webkit-border-color: @color-flame-pea;
      -webkit-background-color: @color-flame-pea;
    }
  }

  &._pulse {
    animation-name: pulse;
    animation-duration: 1s;
    animation-iteration-count: 1;
    animation-direction: alternate;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    animation-delay: 0;
    box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -webkit-animation-name: pulse;
    -webkit-animation-duration: 1s;
    -webkit-animation-iteration-count: 1;
    -webkit-animation-direction: alternate;
    -webkit-animation-timing-function: ease-out;
    -webkit-animation-fill-mode: forwards;
    -webkit-animation-delay: 0;
    -webkit-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
  }

  &._white-bg {
    background-color: #fff;
    color: @color-elephant;
  }

  &._disabled {
    color: @color-silver-chalice;
    background-color: @color-mercury;
    pointer-events: none;
    border-color: transparent;
  }

  &__icon path {
    fill: white;
    transition: all @A ease-in-out;
  }

  &._stroke &__icon {
    path {
      stroke: white;
    }
  }

  &._white-bg &__icon {
    path {
      fill: @color-elephant;
    }
  }

  &._white-bg._stroke &__icon {
    path {
      stroke: @color-elephant;
    }
  }

  &._white-bg:not(._mobile):hover {
    color: #fff;

    .app-button__icon path {
      fill: white;
      -webkit-fill: white;
    }
  }

  &._full {
    width: 100%;
  }

  &__touch {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &._mobile {
    &._touched {
      background-color: @color-flame-pea;
      color: #fff;
    }

    &._touched._link:focus {
      color: #fff;
    }

    &._touched._link:active {
      color: #fff;
    }

    &._touched._link .app__button-text {
      color: #fff;
    }

    &._bordered._touched {
      color: #fff;
      border-color: @color-flame-pea;
      background-color: @color-flame-pea;
    }

    &._white-bg._touched .app-button__icon path {
      fill: white;
    }
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 @color-flame-pea;
    -webkit-box-shadow: 0 0 0 0 @color-flame-pea;
    -moz-box-shadow: 0 0 0 0 @color-flame-pea;
    -o-box-shadow: 0 0 0 0 @color-flame-pea;
    -ms-box-shadow: 0 0 0 0 @color-flame-pea;
  }

  100% {
    box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -webkit-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -moz-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -o-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -ms-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
  }
}

@-webkit-keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 @color-flame-pea;
    -webkit-box-shadow: 0 0 0 0 @color-flame-pea;
  }

  100% {
    box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -webkit-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
  }
}

@-moz-keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 @color-flame-pea;
    -moz-box-shadow: 0 0 0 0 @color-flame-pea;
  }

  100% {
    box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -moz-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
  }
}

@-o-keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 @color-flame-pea;
    -o-box-shadow: 0 0 0 0 @color-flame-pea;
  }

  100% {
    box-shadow: 0 0 0 @size-x3 rgba(white, 0);
    -o-box-shadow: 0 0 0 @size-x3 rgba(white, 0);
  }
}
</style>
